import { COMMA, SPACE } from '@angular/cdk/keycodes';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { ControlContainer, FormControl, FormGroup } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { ThemePalette } from '@angular/material/core';

@Component({
  selector: 'app-input-chips',
  templateUrl: './input-chips.component.html',
  styleUrls: ['./input-chips.component.scss'],
  standalone: false,
})
export class InputChipsComponent implements AfterViewInit, OnChanges {
  private controlContainer = inject(ControlContainer, { optional: true })!;
  private cdr = inject(ChangeDetectorRef);

  public DEFAULT_COLOR = 'default';
  @Input() public label: string | undefined;
  @Input() public placeHolder = '';
  @Input() public separatorKeysCodes: number[] = [SPACE, COMMA];
  @Input() public controlName!: string;
  @Input() public chips!: string[];
  @Input() public color: ThemePalette | string = this.DEFAULT_COLOR;
  @Input() public forceShowAll = false;
  @Input() public maxListHeight = 0;
  @Input() public maxVisibleChips = 0;
  @Input() public maxVisibleRows = 0;
  @Input() public minChipGridHeight = 32;
  @Output() public chipsChange = new EventEmitter<string[]>();
  @ViewChildren('chipItem', { read: ElementRef })
  private chipElements!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('chipInput', { static: false })
  private chipInputRef?: ElementRef<HTMLInputElement>;

  private computedRowLimitedCount: number | null = null;
  private recomputeTimer: any;

  hideInput = false;
  private readonly CHIP_VERTICAL_SPACING = 6;
  private readonly DEFAULT_ROW_HEIGHT = 32;

  add($event: MatChipInputEvent): void {
    const input = $event.chipInput?.inputElement;
    const value = $event.value || '';
    const trimmed = value.trim();

    if (!trimmed) {
      if (input) {
        input.value = '';
      }
      this.control.setValue(null);
      return;
    }

    if (this.control.value !== trimmed) {
      this.control.setValue(trimmed);
    }

    if (this.control.valid) {
      const emailLike = /@/.test(trimmed);
      if (emailLike) {
        const domainPart = trimmed.split('@')[1] || '';
        const tld = domainPart.split('.').pop() || '';
        if (/\d/.test(tld)) {
          const existingErrors = this.control.errors || {};
          this.control.setErrors({ ...existingErrors, tldNumeric: true });
          this.control.markAsTouched();
          this.control.markAsDirty();
          return;
        }
      }
      const isDup = this.chips.some(
        (c) => c.toLowerCase() === trimmed.toLowerCase(),
      );
      if (isDup) {
        if (input) {
          input.value = '';
        }
        return;
      }
      this.chips.push(trimmed);
      this.chipsChange.emit(this.chips);
      if (input) {
        input.value = '';
      }
      this.control.setValue(null);
      this.hideInput = false;
      this.refocusInput();
      requestAnimationFrame(() => this.refocusInput());
    } else {
      if (input) {
        input.value = '';
      }
      this.control.setValue(null);
    }
  }

  remove(index: number): void {
    this.chips.splice(index, 1);
    this.chipsChange.emit(this.chips);
    this.deferRecompute(true);
    this.updateInputVisibility();
    if (this.forceShowAll) {
      this.hideInput = false;
      requestAnimationFrame(() => this.refocusInput());
    }
  }

  get placeholderText(): string {
    return this.chips.length ? '' : this.placeHolder;
  }

  get form(): FormGroup {
    return this.controlContainer.control as FormGroup;
  }

  get control(): FormControl {
    return this.form.get(this.controlName) as FormControl;
  }

  get visibleChips(): string[] {
    if (this.forceShowAll) {
      return this.chips;
    }
    if (this.maxVisibleRows) {
      if (this.chips.length <= this.maxVisibleRows) {
        return this.chips;
      }
      if (
        this.computedRowLimitedCount != null &&
        this.computedRowLimitedCount > 0
      ) {
        return this.chips.slice(0, this.computedRowLimitedCount);
      }
    }
    return this.maxVisibleChips > 0
      ? this.chips.slice(0, this.maxVisibleChips)
      : this.chips;
  }

  get hiddenChipsCount(): number {
    if (this.forceShowAll) {
      return 0;
    }
    if (this.maxVisibleRows) {
      if (this.computedRowLimitedCount === null) {
        return 0;
      }
      const hidden = this.chips.length - this.computedRowLimitedCount;
      return hidden > 0 ? hidden : 0;
    }
    return this.maxVisibleChips > 0
      ? Math.max(0, this.chips.length - this.maxVisibleChips)
      : 0;
  }

  get showMoreChip(): boolean {
    return this.hiddenChipsCount > 0;
  }

  get minHeight(): number {
    return this.minChipGridHeight || this.DEFAULT_ROW_HEIGHT;
  }

  get shouldHideInput(): boolean {
    return this.chips.length > 1 && this.hideInput && !this.control.value;
  }

  trackByIndex(index: number): number {
    return index;
  }

  ngAfterViewInit(): void {
    this.deferRecompute();
    setTimeout(() => this.updateInputVisibility(), 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.chips || changes.maxVisibleRows || changes.forceShowAll) {
      this.deferRecompute();
    }
    if (changes.chips) {
      this.updateInputVisibility();
    }
  }

  private deferRecompute(doublePass = false): void {
    if (!this.maxVisibleRows) {
      this.computedRowLimitedCount = null;
      return;
    }
    if (this.recomputeTimer) {
      clearTimeout(this.recomputeTimer);
    }
    this.recomputeTimer = setTimeout(() => {
      this.computeRowLimitedCount();
      this.recomputeTimer = null;
      if (doublePass) {
        setTimeout(() => this.computeRowLimitedCount(), 16);
      }
    }, 0);
  }

  private computeRowLimitedCount(): void {
    if (!this.maxVisibleRows || !this.chipElements) {
      return;
    }
    const elements = this.chipElements
      .toArray()
      .map((ref) => ref.nativeElement);
    if (!elements.length) {
      this.computedRowLimitedCount = null;
      this.cdr.detectChanges();
      return;
    }
    const rowTops: number[] = [];
    let count = 0;
    for (const el of elements) {
      const top = el.offsetTop;
      if (!rowTops.includes(top)) {
        rowTops.push(top);
      }
      if (rowTops.length <= this.maxVisibleRows) {
        count++;
      } else {
        break;
      }
    }
    if (rowTops.length <= this.maxVisibleRows) {
      this.computedRowLimitedCount = null;
    } else {
      if (count > 0) {
        count = count - 1;
      }
      this.computedRowLimitedCount = count > 0 ? count : null;
    }

    if (elements[0]) {
      this.minChipGridHeight =
        elements[0].offsetHeight + this.CHIP_VERTICAL_SPACING;
    }
    if (!this.minChipGridHeight) {
      this.minChipGridHeight = this.DEFAULT_ROW_HEIGHT;
    }

    this.cdr.detectChanges();
  }

  onChipGridMouseDown(ev: MouseEvent): void {
    const target = ev.target as HTMLElement | null;
    if (
      target?.closest('.mat-mdc-chip-remove') ||
      target?.closest('.chip-text') ||
      target?.closest('.chips-input')
    ) {
      return;
    }
    ev.preventDefault();
    this.hideInput = false;
    this.refocusInput();
  }

  onChipGridKeydown(ev: KeyboardEvent): void {
    const gridEl = (ev.target as HTMLElement).closest(
      '.input-chip-field',
    ) as HTMLElement | null;
    if (!gridEl || !gridEl.classList.contains('scroll-all')) {
      return;
    }
    const step = 32;
    switch (ev.key) {
      case 'ArrowDown':
        gridEl.scrollTop += step;
        ev.preventDefault();
        break;
      case 'ArrowUp':
        gridEl.scrollTop -= step;
        ev.preventDefault();
        break;
      case 'PageDown':
        gridEl.scrollTop += gridEl.clientHeight;
        ev.preventDefault();
        break;
      case 'PageUp':
        gridEl.scrollTop -= gridEl.clientHeight;
        ev.preventDefault();
        break;
      case 'Home':
        gridEl.scrollTop = 0;
        ev.preventDefault();
        break;
      case 'End':
        gridEl.scrollTop = gridEl.scrollHeight;
        ev.preventDefault();
        break;
      default:
        return;
    }
  }

  onChipRemoved(visibleIndex: number): void {
    const value = this.visibleChips[visibleIndex];
    if (value == null) {
      return;
    }
    if (visibleIndex < 0 || visibleIndex >= this.visibleChips.length) {
      return;
    }
    this.remove(visibleIndex);
  }

  onInputBlur(): void {
    this.updateInputVisibility();
    setTimeout(() => {
      this.computeRowLimitedCount();
      this.cdr.detectChanges();
    }, 50);
  }

  private updateInputVisibility(): void {
    const hasValue = this.control?.value;
    if (!hasValue && this.chips.length > 1) {
      this.hideInput = true;
    } else {
      this.hideInput = false;
    }
  }

  private refocusInput(): void {
    const el = this.chipInputRef?.nativeElement;
    if (el) {
      el.focus();
    }
  }

  onRemoveIconMouseDown(ev: MouseEvent, visibleIndex: number): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.onChipRemoved(visibleIndex);
  }

  focusInputFromBadge(): void {
    this.hideInput = false;
    this.refocusInput();
    requestAnimationFrame(() => this.refocusInput());
    this.cdr.detectChanges();
  }

  public focusInput(): void {
    this.hideInput = false;
    this.refocusInput();
    requestAnimationFrame(() => this.refocusInput());
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (
      event.key === 'Backspace' &&
      (!this.control?.value || this.control.value === '') &&
      Array.isArray(this.chips) &&
      this.chips.length > 0
    ) {
      this.remove(this.chips.length - 1);
      event.preventDefault();
      this.hideInput = false;
      setTimeout(() => this.refocusInput(), 0);
    }
  }
}
